import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import styled, { keyframes } from "styled-components";
import { QRCodeCanvas } from "qrcode.react";
import {
  X,
  Sparkles,
  BookOpen,
  IndianRupee,
  Link2,
  Copy,
  MessageCircle,
  CheckCircle,
  Clock,
  QrCode,
  RefreshCw,
  Receipt,
  History,
  Search,
  ChevronDown,
  User,
  Phone,
  Mail,
  FileText,
  Loader2,
  AlertCircle,
  Check,
  ExternalLink,
  Download,
  Share2,
  ArrowLeft,
  CreditCard,
} from "lucide-react";
import {
  AlbumCustomer,
  AlbumPaymentHistoryItem,
  AlbumPaymentHistoryStats,
  StudioUpiDetails,
} from "../../../../Data/transaction.dto";
import {
  getAlbumCustomersAPI,
  createCustomerPaymentLinkApi,
  createAlbumManualPaymentAPI,
  getAlbumPaymentHistoryAPI,
  checkPaymentStatusV2,
  createUpiQrPaymentAPI,
  confirmUpiQrPaymentAPI,
  getStudioUpiVpaAPI,
} from "../../../../Redux/ApiCalls/Dashboard/CustomerTransationApi";
import { showSuccessToast, showErrorToast } from "../../Utlis/Toast";
import {
  ModalOverlay,
  ModalShell,
  ModalHero,
  HeroGlow,
  HeroRow,
  HeroText,
  HeroBadge,
  ModalTitle,
  ModalSubtitle,
  CloseBtn,
  ModalBody,
} from "../../../molecules/Dashboard/EventCalendar/eventModalSharedStyles";
import {
  Field,
  FieldGrid,
  Label,
  Input,
  ErrorSpan,
} from "../../../molecules/Dashboard/Member/memberFormStyles";
import {
  ModalActions,
  SecondaryButton,
  PrimaryButton,
} from "../../../organisms/Dashboard/quotationMasterListShared";
import { StatusPill, AmountCell } from "./transactionTableStyles";

const PLATFORM_FEE = 10;

type ModalTab = "link" | "manual" | "history";

const MANUAL_PAYMENT_METHODS = [
  { value: "cash", label: "Cash" },
  { value: "upi", label: "UPI" },
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "cheque", label: "Cheque" },
  { value: "card", label: "Card" },
  { value: "other", label: "Other" },
];

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}

function formatDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

interface AlbumPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  preselectedAlbumId?: number;
  mode?: "link" | "manual" | "all";
  initialTab?: ModalTab;
}

export default function AlbumPaymentModal({
  isOpen,
  onClose,
  onSuccess,
  preselectedAlbumId,
  mode = "all",
  initialTab,
}: AlbumPaymentModalProps) {
  const defaultTab: ModalTab = mode === "manual" ? "manual" : (initialTab || "link");
  const [activeTab, setActiveTab] = useState<ModalTab>(defaultTab);

  // Album selection state
  const [albumCustomers, setAlbumCustomers] = useState<AlbumCustomer[]>([]);
  const [selectedAlbum, setSelectedAlbum] = useState<AlbumCustomer | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Link generation form state
  const [amount, setAmount] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [description, setDescription] = useState("Album advance payment");
  const [linkLoading, setLinkLoading] = useState(false);
  const [error, setError] = useState("");

  // Step for Tab 1 (Link Generation): "form" | "choose_method"
  const [albumLinkStep, setAlbumLinkStep] = useState<"form" | "choose_method">("form");
  const [upiDetails, setUpiDetails] = useState<StudioUpiDetails | null>(null);

  // Payment method selection: "upi_direct" | "cashfree"
  const [albumPaymentMethod, setAlbumPaymentMethod] = useState<"upi_direct" | "cashfree">("upi_direct");
  const [albumUtrNumber, setAlbumUtrNumber] = useState("");
  const [albumConfirmingUtr, setAlbumConfirmingUtr] = useState(false);
  const [albumUtrSuccess, setAlbumUtrSuccess] = useState(false);

  // Generated link result
  const [generatedLinkData, setGeneratedLinkData] = useState<{
    short_url: string;
    upi_link?: string;
    secret_token?: string;
    payment_id?: number;
    reference_id?: string;
    amount: number;
    status?: string;
    payment_method?: string;
    payee_vpa?: string;
    payee_name?: string;
    payment_confirmed?: boolean;
    utr?: string;
  } | null>(null);
  const [showQR, setShowQR] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [verifiedStatus, setVerifiedStatus] = useState<string | null>(null);
  const [copiedAlbumImage, setCopiedAlbumImage] = useState(false);

  // Manual payment form state
  const [manualAmount, setManualAmount] = useState("");
  const [manualCustomerName, setManualCustomerName] = useState("");
  const [manualContact, setManualContact] = useState("");
  const [manualMethod, setManualMethod] = useState("cash");
  const [manualStatus, setManualStatus] = useState("Completed");
  const [manualTxnRef, setManualTxnRef] = useState("");
  const [manualDesc, setManualDesc] = useState("Manual payment received");
  const [manualLoading, setManualLoading] = useState(false);
  const [manualSuccessMsg, setManualSuccessMsg] = useState("");

  // Album payment history state
  const [historyStats, setHistoryStats] = useState<AlbumPaymentHistoryStats | null>(null);
  const [historyItems, setHistoryItems] = useState<AlbumPaymentHistoryItem[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [checkingHistoryId, setCheckingHistoryId] = useState<number | null>(null);

  // Close dropdown on outside click
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch album customers list
  const fetchAlbums = async (query = "") => {
    setIsSearching(true);
    try {
      const res = await getAlbumCustomersAPI({
        search: query,
        page: 1,
        page_size: 50,
      });
      const list = res?.data?.results ?? res?.results ?? [];
      setAlbumCustomers(list);

      // If preselectedAlbumId is provided, select it
      if (preselectedAlbumId && !selectedAlbum) {
        const match = list.find((a: AlbumCustomer) => a.album_id === preselectedAlbumId);
        if (match) handleSelectAlbum(match);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSearching(false);
    }
  };

  useEffect(() => {
    const defaultTab: ModalTab = mode === "manual" ? "manual" : (initialTab || "link");
    if (isOpen) {
      setActiveTab(defaultTab);
      fetchStudioUpiDetails();
      fetchAlbums();
    } else {
      // Reset state on close
      setSelectedAlbum(null);
      setSearchQuery("");
      setAmount("");
      setCustomerName("");
      setContact("");
      setEmail("");
      setDescription("Album advance payment");
      setGeneratedLinkData(null);
      setShowQR(false);
      setError("");
      setVerifiedStatus(null);
      setManualAmount("");
      setManualCustomerName("");
      setManualContact("");
      setManualTxnRef("");
      setManualSuccessMsg("");
      setHistoryStats(null);
      setHistoryItems([]);
      setActiveTab(defaultTab);
      setAlbumLinkStep("form");
      setAlbumUtrNumber("");
      setAlbumUtrSuccess(false);
    }
  }, [isOpen, mode, initialTab]);

  const fetchStudioUpiDetails = async () => {
    try {
      const res = await getStudioUpiVpaAPI();
      if (res && res.data) {
        setUpiDetails(res.data);
      }
    } catch {
      // ignore
    }
  };

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;
    const timeout = setTimeout(() => {
      fetchAlbums(searchQuery);
    }, 350);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Select album handler
  const handleSelectAlbum = (album: AlbumCustomer) => {
    setSelectedAlbum(album);
    setIsDropdownOpen(false);
    setError("");
    setManualSuccessMsg("");
    setGeneratedLinkData(null);

    // Prefill customer details
    setCustomerName(album.customer_name || "");
    setContact(album.customer_mobile || "");
    setManualCustomerName(album.customer_name || "");
    setManualContact(album.customer_mobile || "");

    // If on history tab, load history
    if (activeTab === "history") {
      fetchAlbumHistory(album.album_id);
    }
  };

  // Fetch album payment history
  const fetchAlbumHistory = async (albumId: number) => {
    setHistoryLoading(true);
    try {
      const res = await getAlbumPaymentHistoryAPI({ album_id: albumId });
      const data = res?.data ?? res;
      setHistoryStats(data?.stats ?? null);
      setHistoryItems(data?.results ?? []);
    } catch (err) {
      console.error(err);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Switch tabs
  const handleTabChange = (tab: ModalTab) => {
    setActiveTab(tab);
    setError("");
    setAlbumLinkStep("form");
    if (tab === "history" && selectedAlbum) {
      fetchAlbumHistory(selectedAlbum.album_id);
    }
  };

  // Step 1 -> Step 2: Validate details and proceed to payment method selection
  const handleProceedToChooseMethod = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAlbum) {
      setError("Please select an album customer first.");
      return;
    }
    const numAmount = Number(amount);
    if (!amount || isNaN(numAmount) || numAmount <= 0) {
      setError("Please enter a valid amount greater than 0.");
      return;
    }
    if (numAmount > 100000) {
      setError("Amount cannot exceed ₹1,00,000 for UPI.");
      return;
    }
    const cleanMobile = contact.replace(/\D/g, "");
    if (!cleanMobile) {
      setError("Customer mobile number is compulsory.");
      return;
    }
    if (cleanMobile.length !== 10 || !/^[6-9]\d{9}$/.test(cleanMobile)) {
      setError("Please enter a valid 10-digit customer mobile number starting with 6-9.");
      return;
    }

    setError("");
    setAlbumLinkStep("choose_method");
  };

  // Handle Online Link / QR Generation from Step 2
  const handleGenerateLink = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!selectedAlbum) {
      setError("Please select an album customer first.");
      return;
    }
    const numAmount = Number(amount);
    if (!amount || isNaN(numAmount) || numAmount <= 0) {
      setError("Please enter a valid amount greater than 0.");
      return;
    }
    if (albumPaymentMethod === "cashfree" && numAmount <= PLATFORM_FEE) {
      setError(`Cashfree amount must be greater than ₹${PLATFORM_FEE} (platform fee).`);
      return;
    }
    if (numAmount > 100000) {
      setError("Amount cannot exceed ₹1,00,000 for UPI.");
      return;
    }
    const cleanMobile = contact.replace(/\D/g, "");
    if (!cleanMobile) {
      setError("Customer mobile number is compulsory.");
      return;
    }
    if (cleanMobile.length !== 10 || !/^[6-9]\d{9}$/.test(cleanMobile)) {
      setError("Please enter a valid 10-digit customer mobile number starting with 6-9.");
      return;
    }

    const finalCustomerName = customerName.trim() || selectedAlbum.customer_name || "";
    const nameParts = finalCustomerName.split(" ");
    const fName = nameParts[0] || "";
    const lName = nameParts.slice(1).join(" ") || "";

    setError("");
    setLinkLoading(true);

    if (albumPaymentMethod === "upi_direct") {
      try {
        const res = await createUpiQrPaymentAPI({
          amount: numAmount,
          album_id: selectedAlbum.album_id,
          customer_name: finalCustomerName,
          contact_details: cleanMobile,
          note: (description.trim() || `Payment for album ${selectedAlbum.album_name}`).slice(0, 50),
        });

        if (res && res.upi_link) {
          setGeneratedLinkData({
            short_url: res.upi_link,
            upi_link: res.upi_link,
            secret_token: "",
            payment_id: res.payment_id,
            reference_id: res.reference_id,
            amount: numAmount,
            status: res.status || "pending",
            payment_method: "upi_direct",
            payee_vpa: res.payee_vpa,
            payee_name: res.payee_name,
            payment_confirmed: false,
          });
          setShowQR(true);
          showSuccessToast("Album Direct UPI QR generated successfully!");
          onSuccess?.();
        } else if (res?.code === "upi_vpa_missing") {
          setError("Studio UPI ID is not set. Please set your UPI ID in dashboard or bank settings.");
        }
      } catch (err) {
        console.error(err);
        setError("Failed to generate UPI QR. Please try again.");
      } finally {
        setLinkLoading(false);
      }
      return;
    }

    try {
      const res = await createCustomerPaymentLinkApi({
        amount: numAmount,
        album_id: selectedAlbum.album_id,
        customer_name: finalCustomerName,
        first_name: fName || finalCustomerName,
        last_name: lName,
        contact_details: contact.trim() || selectedAlbum.customer_mobile,
        email: email.trim() || undefined,
        description: description.trim() || `Payment for album ${selectedAlbum.album_name}`,
        payment_provider: "cashfree",
        host_name: window.location.origin,
      });

      if (res && res.short_url) {
        setGeneratedLinkData({
          short_url: res.short_url,
          secret_token: res.secret_token,
          payment_id: res.payment_id,
          amount: numAmount,
          status: res.status || "created",
          payment_method: "cashfree",
        });
        showSuccessToast("Album payment link generated successfully!");
        onSuccess?.();
      }
    } catch (err) {
      console.error(err);
      setError("Failed to generate payment link. Please check details and try again.");
    } finally {
      setLinkLoading(false);
    }
  };

  const handleConfirmAlbumUpi = async () => {
    if (!generatedLinkData?.payment_id) return;
    const cleanUtr = albumUtrNumber.trim().replace(/\s+/g, "");
    if (!cleanUtr) {
      showErrorToast("Please enter the 12-digit UTR reference.");
      return;
    }
    if (cleanUtr.length < 12 || cleanUtr.length > 35) {
      showErrorToast("UTR number must be at least 12 digits.");
      return;
    }
    setAlbumConfirmingUtr(true);
    try {
      const res = await confirmUpiQrPaymentAPI(generatedLinkData.payment_id, {
        status: "paid",
        utr: cleanUtr,
      });
      if (res && res.status === "paid") {
        setGeneratedLinkData((prev) =>
          prev ? { ...prev, status: "paid", payment_confirmed: true, utr: cleanUtr } : null
        );
        setAlbumUtrSuccess(true);
        showSuccessToast("Payment confirmed as PAID!");
        onSuccess?.();
      }
    } catch {
      showErrorToast("Could not confirm UPI payment.");
    } finally {
      setAlbumConfirmingUtr(false);
    }
  };

  // Check payment status with token
  const handleCheckStatus = async () => {
    if (!generatedLinkData?.secret_token) return;
    setCheckingStatus(true);
    try {
      const res = await checkPaymentStatusV2(generatedLinkData.secret_token);
      const status = res?.status || res?.data?.status || "checked";
      setVerifiedStatus(status);
      showSuccessToast(`Payment status: ${status.toUpperCase()}`);
      if (status.toLowerCase() === "paid") {
        onSuccess?.();
      }
    } catch (err) {
      console.error(err);
      showErrorToast("Could not verify payment status.");
    } finally {
      setCheckingStatus(false);
    }
  };

  // Check status in history table
  const handleCheckHistoryItemStatus = async (item: AlbumPaymentHistoryItem) => {
    if (!item.secret_token) {
      showErrorToast("No secret token available for this transaction.");
      return;
    }
    setCheckingHistoryId(item.payment_id);
    try {
      const res = await checkPaymentStatusV2(item.secret_token);
      const status = res?.status || res?.data?.status || "checked";
      showSuccessToast(`Payment #${item.payment_id} status: ${status.toUpperCase()}`);
      if (selectedAlbum) {
        fetchAlbumHistory(selectedAlbum.album_id);
      }
      onSuccess?.();
    } catch (err) {
      console.error(err);
      showErrorToast("Could not verify status.");
    } finally {
      setCheckingHistoryId(null);
    }
  };

  // Copy link
  const handleCopyLink = async () => {
    if (!generatedLinkData?.short_url) return;
    try {
      await navigator.clipboard.writeText(generatedLinkData.short_url);
      showSuccessToast("Payment link copied to clipboard!");
    } catch {
      showErrorToast("Could not copy link.");
    }
  };

  // Direct UPI QR Card image blob generator
  const getAlbumQrCardBlob = async (): Promise<Blob | null> => {
    const qrCanvas = document.getElementById("album-direct-upi-qrcode-canvas") as HTMLCanvasElement | null;
    if (!qrCanvas) return null;

    const cardCanvas = document.createElement("canvas");
    const ctx = cardCanvas.getContext("2d");
    if (!ctx) return null;

    const width = 420;
    const height = 430;
    cardCanvas.width = width;
    cardCanvas.height = height;

    // Card background
    ctx.fillStyle = "#ffffff";
    if (typeof (ctx as any).roundRect === "function") {
      ctx.beginPath();
      (ctx as any).roundRect(0, 0, width, height, 20);
      ctx.fill();
    } else {
      ctx.fillRect(0, 0, width, height);
    }

    // Card border
    ctx.strokeStyle = "rgba(23, 23, 23, 0.08)";
    ctx.lineWidth = 2;
    if (typeof (ctx as any).roundRect === "function") {
      ctx.beginPath();
      (ctx as any).roundRect(0, 0, width, height, 20);
      ctx.stroke();
    }

    // Gradient bar at top
    const grad = ctx.createLinearGradient(0, 0, width, 0);
    grad.addColorStop(0, "#ffc951");
    grad.addColorStop(1, "#ce56b0");
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, width, 8);

    // Title: Amount
    ctx.fillStyle = "#171717";
    ctx.font = "bold 24px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    ctx.textAlign = "center";
    const amountVal = generatedLinkData?.amount ? Number(generatedLinkData.amount).toFixed(2) : "0.00";
    ctx.fillText(`Scan to Pay ₹${amountVal}`, width / 2, 48);

    // QR container box
    const qrSize = 250;
    const qrX = (width - qrSize) / 2;
    const qrY = 74;

    ctx.fillStyle = "#fafafa";
    if (typeof (ctx as any).roundRect === "function") {
      ctx.beginPath();
      (ctx as any).roundRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20, 16);
      ctx.fill();
      ctx.strokeStyle = "rgba(0, 0, 0, 0.06)";
      ctx.stroke();
    } else {
      ctx.fillRect(qrX - 10, qrY - 10, qrSize + 20, qrSize + 20);
    }

    // Draw the QR canvas
    ctx.drawImage(qrCanvas, qrX, qrY, qrSize, qrSize);

    // UPI ID
    ctx.fillStyle = "#0f172a";
    ctx.font = "bold 14px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    ctx.fillText(`UPI ID: ${generatedLinkData?.payee_vpa || ""}`, width / 2, qrY + qrSize + 36);

    // Reference ID
    ctx.fillStyle = "#64748b";
    ctx.font = "500 12px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
    const albumRef = generatedLinkData?.reference_id || (selectedAlbum ? `Album #${selectedAlbum.album_id}` : "");
    ctx.fillText(`Ref: ${albumRef}`, width / 2, qrY + qrSize + 58);

    return new Promise((resolve) => {
      cardCanvas.toBlob((blob) => resolve(blob), "image/png");
    });
  };

  const handleDownloadAlbumQr = async () => {
    try {
      const blob = await getAlbumQrCardBlob();
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const amountStr = generatedLinkData?.amount ? Number(generatedLinkData.amount).toFixed(0) : "0";
      a.download = `UPI_QR_₹${amountStr}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showSuccessToast("QR code image downloaded!");
    } catch (err) {
      console.error(err);
      showErrorToast("Could not download QR image.");
    }
  };

  const handleCopyAlbumQrImage = async () => {
    try {
      const blob = await getAlbumQrCardBlob();
      if (!blob) return;
      if (navigator.clipboard && (window as any).ClipboardItem) {
        await navigator.clipboard.write([
          new (window as any).ClipboardItem({ "image/png": blob }),
        ]);
        setCopiedAlbumImage(true);
        setTimeout(() => setCopiedAlbumImage(false), 2500);
        showSuccessToast("QR image copied! Paste (Ctrl+V) directly to share.");
      } else {
        handleDownloadAlbumQr();
      }
    } catch (err) {
      console.error(err);
      handleDownloadAlbumQr();
    }
  };

  const handleShareAlbumDirectUpiQr = async () => {
    if (!generatedLinkData) return;

    try {
      const blob = await getAlbumQrCardBlob();
      if (blob) {
        const file = new File(
          [blob],
          `UPI_QR_₹${Number(generatedLinkData.amount).toFixed(0)}.png`,
          { type: "image/png" }
        );

        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({
            title: "UPI Payment QR",
            text: `Please scan this QR code to pay ₹${Number(generatedLinkData.amount).toFixed(2)} directly via UPI (${generatedLinkData.payee_vpa}).`,
            files: [file],
          });
          return;
        }
      }
    } catch (err) {
      if ((err as Error)?.name === "AbortError") return;
    }

    try {
      const blob = await getAlbumQrCardBlob();
      if (blob && navigator.clipboard && (window as any).ClipboardItem) {
        await navigator.clipboard.write([
          new (window as any).ClipboardItem({ "image/png": blob }),
        ]);
        showSuccessToast("QR image copied to clipboard! Paste (Ctrl+V) to share.");
      }
    } catch {
      // Ignore
    }

    const clientName = customerName.trim() || selectedAlbum?.customer_name || "Customer";
    const phoneClean = contact.replace(/\D/g, "");
    if (phoneClean) {
      const msg = `Hello ${clientName}, please scan the UPI QR code to pay ₹${Number(generatedLinkData.amount).toFixed(2)} for album "${selectedAlbum?.album_name || ""}".\n\n(QR image has been copied to your clipboard — press Paste / Ctrl+V in this chat!)`;
      const waUrl = `https://wa.me/${phoneClean.length === 10 ? `91${phoneClean}` : phoneClean}?text=${encodeURIComponent(msg)}`;
      window.open(waUrl, "_blank");
    }
  };

  // Share via WhatsApp (Cashfree hosted link)
  const handleShareWhatsApp = () => {
    if (!generatedLinkData?.short_url || !selectedAlbum) return;
    const clientName = customerName.trim() || selectedAlbum.customer_name || "Customer";
    const msg = `Hello ${clientName}, please complete your payment of ${formatCurrency(
      generatedLinkData.amount
    )} for album "${selectedAlbum.album_name}". Click here to pay: ${generatedLinkData.short_url}`;
    const phoneClean = contact.replace(/\D/g, "");
    const waUrl = phoneClean
      ? `https://wa.me/${phoneClean.length === 10 ? `91${phoneClean}` : phoneClean}?text=${encodeURIComponent(
          msg
        )}`
      : `https://wa.me/?text=${encodeURIComponent(msg)}`;
    window.open(waUrl, "_blank");
  };

  // Record manual payment handler
  const handleManualPaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedAlbum) {
      setError("Please select an album customer first.");
      return;
    }
    const numAmount = Number(manualAmount);
    if (!manualAmount || isNaN(numAmount) || numAmount <= 0) {
      setError("Please enter a valid amount greater than 0.");
      return;
    }

    const finalCustomerName = manualCustomerName.trim() || selectedAlbum.customer_name || "";
    const cleanMobile = manualContact.replace(/\D/g, "");

    if (finalCustomerName && !cleanMobile) {
      setError("Mobile number is compulsory when customer name is entered.");
      return;
    }
    if (cleanMobile && (cleanMobile.length !== 10 || !/^[6-9]\d{9}$/.test(cleanMobile))) {
      setError("Please enter a valid 10-digit customer mobile number starting with 6-9.");
      return;
    }

    setError("");
    setManualLoading(true);
    setManualSuccessMsg("");
    try {
      const res = await createAlbumManualPaymentAPI({
        album_id: selectedAlbum.album_id,
        amount: numAmount,
        payment_method: manualMethod,
        payment_status: manualStatus,
        customer_name: finalCustomerName || undefined,
        contact_details: cleanMobile || undefined,
        transaction_reference: manualTxnRef.trim() || undefined,
        description: manualDesc.trim() || undefined,
      });

      if (res != null) {
        setManualSuccessMsg(`Successfully recorded ₹${numAmount} manual payment!`);
        showSuccessToast("Manual payment recorded successfully.");
        setManualAmount("");
        setManualTxnRef("");
        onSuccess?.();
      }
    } catch (err) {
      console.error(err);
      setError("Failed to record manual payment.");
    } finally {
      setManualLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <ModalOverlay onClick={onClose}>
      <ModalShell
        style={{ maxWidth: 660 }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="album-payment-modal-title"
      >
        <ModalHero>
          <HeroGlow />
          <HeroRow>
            <HeroLeftWrap>
              {activeTab === "link" && albumLinkStep === "choose_method" && !generatedLinkData && (
                <HeaderBackBtn
                  type="button"
                  onClick={() => setAlbumLinkStep("form")}
                  aria-label="Back to details"
                  disabled={linkLoading}
                >
                  <ArrowLeft size={18} />
                </HeaderBackBtn>
              )}
              <HeroText>
                <HeroBadge>
                  <Sparkles size={11} />
                  {activeTab === "link" && albumLinkStep === "choose_method" && !generatedLinkData
                    ? "Payment Options"
                    : mode === "manual"
                    ? "Manual Payment"
                    : mode === "link"
                    ? "Payment Link"
                    : "Existing Customer Payments"}
                </HeroBadge>
                <ModalTitle id="album-payment-modal-title">
                  {activeTab === "link" && albumLinkStep === "choose_method" && !generatedLinkData
                    ? "Choose Payment Method"
                    : mode === "manual"
                    ? "Existing Customer Manual Payment"
                    : mode === "link"
                    ? "Existing Customer Payment Link"
                    : "Existing Customer Payment"}
                </ModalTitle>
                <ModalSubtitle>
                  {activeTab === "link" && albumLinkStep === "choose_method" && !generatedLinkData
                    ? `Select how you'd like to collect ₹${Number(amount || 0).toFixed(2)} from ${customerName || selectedAlbum?.customer_name || "customer"}.`
                    : mode === "manual"
                    ? "Record an offline payment (Cash, UPI, Bank Transfer, etc.) for an existing album customer."
                    : mode === "link"
                    ? "Generate payment links or direct UPI QR for an existing album customer."
                    : "Generate payment links or record manual payments for existing album customers."}
                </ModalSubtitle>
              </HeroText>
            </HeroLeftWrap>
            <CloseBtn type="button" onClick={onClose} aria-label="Close modal">
              <X size={18} />
            </CloseBtn>
          </HeroRow>
        </ModalHero>

        <ModalBody style={{ padding: "20px 24px" }}>
          {!(activeTab === "link" && albumLinkStep === "choose_method" && !generatedLinkData) && (
            <>
              {/* Sub-Tabs */}
              <NavTabs>
                {mode !== "manual" && (
                  <NavTabButton
                    type="button"
                    $active={activeTab === "link"}
                    onClick={() => handleTabChange("link")}
                  >
                    <Link2 size={15} />
                    <span>Payment Link</span>
                  </NavTabButton>
                )}
                {mode !== "link" && (
                  <NavTabButton
                    type="button"
                    $active={activeTab === "manual"}
                    onClick={() => handleTabChange("manual")}
                  >
                    <Receipt size={15} />
                    <span>Manual Payment</span>
                  </NavTabButton>
                )}
                <NavTabButton
                  type="button"
                  $active={activeTab === "history"}
                  onClick={() => handleTabChange("history")}
                >
                  <History size={15} />
                  <span>Payment History</span>
                </NavTabButton>
              </NavTabs>

              {/* Album Picker Section (common across tabs) */}
              <AlbumSelectorSection ref={dropdownRef}>
                <Label style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Select Album Customer <ReqStar>*</ReqStar></span>
                  {selectedAlbum && (
                    <ChangeAlbumLink type="button" onClick={() => setIsDropdownOpen(true)}>
                      Change album
                    </ChangeAlbumLink>
                  )}
                </Label>

                {!selectedAlbum ? (
                  <PickerInputWrapper>
                    <SearchIconWrap>
                      <Search size={16} />
                    </SearchIconWrap>
                    <AlbumSearchInput
                      type="text"
                      placeholder="Search by customer name, mobile, or album name..."
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setIsDropdownOpen(true);
                      }}
                      onFocus={() => setIsDropdownOpen(true)}
                    />
                    {isSearching ? (
                      <SpinWrap>
                        <Loader2 size={16} />
                      </SpinWrap>
                    ) : (
                      <DropdownToggle
                        type="button"
                        onClick={() => setIsDropdownOpen((prev) => !prev)}
                      >
                        <ChevronDown size={18} />
                      </DropdownToggle>
                    )}
                  </PickerInputWrapper>
                ) : (
                  <SelectedAlbumCard>
                    <AlbumCardLeft>
                      <AlbumIconBadge>
                        <BookOpen size={18} />
                      </AlbumIconBadge>
                      <AlbumInfo>
                        <AlbumNameRow>
                          <AlbumName>{selectedAlbum.album_name}</AlbumName>
                          <AlbumIdBadge>ID: #{selectedAlbum.album_id}</AlbumIdBadge>
                        </AlbumNameRow>
                        <CustomerMeta>
                          <span>{selectedAlbum.customer_name || "Customer"}</span>
                          {selectedAlbum.customer_mobile && (
                            <>
                              <DotSeparator>•</DotSeparator>
                              <span>{selectedAlbum.customer_mobile}</span>
                            </>
                          )}
                        </CustomerMeta>
                      </AlbumInfo>
                    </AlbumCardLeft>
                    <AlbumPaidSummary>
                      <SummaryAmount>
                        {formatCurrency(selectedAlbum.paid_amount || 0)}
                      </SummaryAmount>
                      <SummaryLabel>Paid so far</SummaryLabel>
                    </AlbumPaidSummary>
                  </SelectedAlbumCard>
                )}

                {/* Dropdown Results */}
                {isDropdownOpen && (
                  <DropdownList>
                    {albumCustomers.length === 0 ? (
                      <EmptyDropdown>
                        {isSearching ? "Searching albums..." : "No albums found with customer details."}
                      </EmptyDropdown>
                    ) : (
                      albumCustomers.map((album) => (
                        <DropdownItem
                          key={album.album_id}
                          type="button"
                          onClick={() => handleSelectAlbum(album)}
                        >
                          <DropdownItemLeft>
                            <DropdownItemTitle>{album.album_name}</DropdownItemTitle>
                            <DropdownItemSub>
                              {album.customer_name} {album.customer_mobile ? `(${album.customer_mobile})` : ""}
                            </DropdownItemSub>
                          </DropdownItemLeft>
                          <DropdownItemRight>
                            <PaidPill>Paid {formatCurrency(album.paid_amount || 0)}</PaidPill>
                          </DropdownItemRight>
                        </DropdownItem>
                      ))
                    )}
                  </DropdownList>
                )}
              </AlbumSelectorSection>
            </>
          )}

          {error && (
            <ErrorBanner>
              <AlertCircle size={16} />
              <span>{error}</span>
            </ErrorBanner>
          )}

          {/* TAB 1: ONLINE PAYMENT LINK */}
          {activeTab === "link" && (
            <>
              {!generatedLinkData ? (
                albumLinkStep === "form" ? (
                  <form onSubmit={handleProceedToChooseMethod}>
                    <Field style={{ marginTop: 16 }}>
                      <Label>
                        Amount (INR) <ReqStar>*</ReqStar>
                      </Label>
                      <Input
                        type="number"
                        placeholder="e.g. 5000"
                        value={amount}
                        min={1}
                        onChange={(e) => setAmount(e.target.value)}
                        required
                      />
                    </Field>

                    <Field style={{ marginTop: 14 }}>
                      <Label>Customer Name</Label>
                      <Input
                        type="text"
                        placeholder="e.g. Customer Name"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                      />
                    </Field>

                    <FieldGrid style={{ marginTop: 14 }}>
                      <Field>
                        <Label>
                          Mobile Number <ReqStar>*</ReqStar>
                        </Label>
                        <Input
                          type="tel"
                          placeholder="10-digit mobile"
                          value={contact}
                          onChange={(e) => setContact(e.target.value)}
                          required
                        />
                      </Field>
                      <Field>
                        <Label>Customer Email (Optional)</Label>
                        <Input
                          type="email"
                          placeholder="customer@example.com"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                        />
                      </Field>
                    </FieldGrid>

                    <Field style={{ marginTop: 14 }}>
                      <Label>Description / Notes</Label>
                      <Input
                        type="text"
                        placeholder="e.g. Album advance payment"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                      />
                    </Field>

                    <ModalActions style={{ marginTop: 22 }}>
                      <SecondaryButton type="button" onClick={onClose}>
                        Cancel
                      </SecondaryButton>
                      <PrimaryButton
                        type="submit"
                        disabled={!selectedAlbum || !amount}
                      >
                        <Link2 size={16} />
                        <span>Generate Payment Link</span>
                      </PrimaryButton>
                    </ModalActions>
                  </form>
                ) : (
                  <div>
                    <OptionsGrid style={{ marginTop: 12 }}>
                      {/* OPTION 1: DIRECT UPI QR */}
                      <OptionCard
                        $selected={albumPaymentMethod === "upi_direct"}
                        $tone="purple"
                        onClick={() => setAlbumPaymentMethod("upi_direct")}
                      >
                        <OptionHeader>
                          <OptionTitleWrap>
                            <OptionIcon $tone="purple">
                              <QrCode size={20} />
                            </OptionIcon>
                            <div>
                              <OptionTitle>Direct UPI QR</OptionTitle>
                            </div>
                          </OptionTitleWrap>
                          <OptionBadge $tone="purple">Direct to Bank</OptionBadge>
                        </OptionHeader>

                        <OptionDesc>
                          Customer scans with Google Pay, PhonePe, Paytm, BHIM, Cred, etc. Money settles
                          instantly in your bank account.
                        </OptionDesc>

                        {upiDetails?.has_upi_vpa ? (
                          <UpiVpaBox>
                            <div>
                              Receiving on: <strong>{upiDetails.upi_vpa}</strong>
                            </div>
                          </UpiVpaBox>
                        ) : (
                          <div
                            style={{
                              marginTop: 10,
                              padding: "10px 12px",
                              borderRadius: 10,
                              background: "rgba(228, 72, 98, 0.06)",
                              border: "1px solid rgba(228, 72, 98, 0.2)",
                              fontSize: 12,
                              color: "#be123c",
                              lineHeight: 1.4,
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            Studio UPI ID is not configured. Please set your UPI ID in the Bank Account section before selecting this option.
                          </div>
                        )}

                        <OptionAmountRow>
                          <span>Platform Fee: ₹0.00</span>
                          <div>
                            Customer Pays: <strong>₹{Number(amount || 0).toFixed(2)}</strong>
                          </div>
                        </OptionAmountRow>
                      </OptionCard>

                      {/* OPTION 2: CASHFREE GATEWAY */}
                      <OptionCard
                        $selected={albumPaymentMethod === "cashfree"}
                        $tone="purple"
                        onClick={() => setAlbumPaymentMethod("cashfree")}
                      >
                        <OptionHeader>
                          <OptionTitleWrap>
                            <OptionIcon $tone="purple">
                              <CreditCard size={20} />
                            </OptionIcon>
                            <div>
                              <OptionTitle>Cashfree Payment Gateway</OptionTitle>
                            </div>
                          </OptionTitleWrap>
                          <OptionBadge $tone="purple">Auto-verified · ₹{PLATFORM_FEE} platform fee</OptionBadge>
                        </OptionHeader>

                        <OptionDesc>
                          Hosted payment link supporting Credit Cards, Debit Cards, Netbanking & UPI with
                          automated status confirmation and webhook callbacks.
                        </OptionDesc>

                        <OptionAmountRow>
                          <span>Platform Fee: ₹{PLATFORM_FEE}</span>
                          <div>
                            Customer Pays: <strong>₹{(Number(amount || 0) + PLATFORM_FEE).toFixed(2)}</strong>
                          </div>
                        </OptionAmountRow>
                      </OptionCard>
                    </OptionsGrid>

                    <ModalActions style={{ marginTop: 22 }}>
                      <SecondaryButton
                        type="button"
                        onClick={() => setAlbumLinkStep("form")}
                        disabled={linkLoading}
                      >
                        Back
                      </SecondaryButton>
                      <PrimaryButton
                        type="button"
                        onClick={() => handleGenerateLink()}
                        disabled={linkLoading || (albumPaymentMethod === "upi_direct" && !upiDetails?.has_upi_vpa)}
                      >
                        {linkLoading ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            <span>Generating...</span>
                          </>
                        ) : (
                          <>
                            {albumPaymentMethod === "upi_direct" ? <QrCode size={16} /> : <Link2 size={16} />}
                            <span>
                              {albumPaymentMethod === "upi_direct"
                                ? "Generate Direct UPI QR"
                                : "Generate Cashfree Link"}
                            </span>
                          </>
                        )}
                      </PrimaryButton>
                    </ModalActions>
                  </div>
                )
              ) : (
                /* Success View with Generated Link & QR Code */
                <SuccessSection>
                  <SuccessBanner>
                    <SuccessIconWrap>
                      <CheckCircle size={22} />
                    </SuccessIconWrap>
                    <div>
                      <SuccessTitle>
                        {generatedLinkData.payment_method === "upi_direct"
                          ? "Direct UPI QR Generated!"
                          : "Payment Link Generated!"}
                      </SuccessTitle>
                      <SuccessSub>
                        {generatedLinkData.payment_method === "upi_direct"
                          ? `Customer can scan with any UPI app to pay ₹${generatedLinkData.amount.toFixed(2)} directly to your bank account.`
                          : "Share this Cashfree hosted checkout link with your customer."}
                      </SuccessSub>
                    </div>
                  </SuccessBanner>

                  {generatedLinkData.payment_method === "upi_direct" ? (
                    <>
                      <QrWrap>
                        <QRCodeCanvas
                          id="album-direct-upi-qrcode-canvas"
                          value={generatedLinkData.upi_link || generatedLinkData.short_url}
                          size={200}
                          bgColor="#ffffff"
                          fgColor="#171717"
                          level="H"
                          includeMargin
                        />
                        <div style={{ textAlign: "center", marginTop: 8 }}>
                          <div style={{ fontSize: 13, fontWeight: 700, color: "#171717" }}>
                            Scan with any UPI app to pay
                          </div>
                          <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                            UPI ID: <strong>{generatedLinkData.payee_vpa}</strong>
                          </div>
                          {generatedLinkData.reference_id && (
                            <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>
                              Ref: {generatedLinkData.reference_id}
                            </div>
                          )}
                        </div>
                      </QrWrap>

                      <ActionGrid>
                        <ActionBtn type="button" onClick={handleShareAlbumDirectUpiQr}>
                          <Share2 size={15} />
                          <span>Share QR</span>
                        </ActionBtn>
                        <ActionBtn type="button" onClick={handleDownloadAlbumQr}>
                          <Download size={15} />
                          <span>Download QR</span>
                        </ActionBtn>
                        <ActionBtn type="button" onClick={handleCopyAlbumQrImage}>
                          {copiedAlbumImage ? <CheckCircle size={15} /> : <Copy size={15} />}
                          <span>{copiedAlbumImage ? "Copied" : "Copy Image"}</span>
                        </ActionBtn>
                      </ActionGrid>
                    </>
                  ) : (
                    <>
                      <LinkBoxWrap>
                        <LinkBoxText>{generatedLinkData.short_url}</LinkBoxText>
                        <CopyBtn type="button" onClick={handleCopyLink} title="Copy link">
                          <Copy size={16} />
                          <span>Copy</span>
                        </CopyBtn>
                      </LinkBoxWrap>

                      <ActionGrid>
                        <ActionBtn $solid $tone="#25d366" type="button" onClick={handleShareWhatsApp}>
                          <MessageCircle size={16} />
                          <span>Share on WhatsApp</span>
                        </ActionBtn>
                        <ActionBtn
                          type="button"
                          onClick={() => setShowQR((prev) => !prev)}
                          $active={showQR}
                        >
                          <QrCode size={16} />
                          <span>{showQR ? "Hide QR" : "Show QR Code"}</span>
                        </ActionBtn>
                        <ActionBtn
                          type="button"
                          onClick={handleCheckStatus}
                          disabled={checkingStatus}
                        >
                          <RefreshCw size={16} className={checkingStatus ? "animate-spin" : ""} />
                          <span>{checkingStatus ? "Checking..." : "Check Status"}</span>
                        </ActionBtn>
                      </ActionGrid>

                      {showQR && (
                        <QrWrap>
                          <QRCodeCanvas
                            value={generatedLinkData.short_url}
                            size={170}
                            bgColor="#ffffff"
                            fgColor="#171717"
                            level="H"
                            includeMargin
                          />
                        </QrWrap>
                      )}
                    </>
                  )}

                  {/* UTR Reconciliation for Direct UPI */}
                  {generatedLinkData.payment_method === "upi_direct" && (
                    <div
                      style={{
                        padding: 14,
                        borderRadius: 14,
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          fontWeight: 700,
                          fontSize: 13,
                        }}
                      >
                        <span>Reconciliation & Confirmation</span>
                        {(generatedLinkData.payment_confirmed || albumUtrSuccess) && (
                          <span style={{ color: "#15803d", fontSize: 12 }}>✓ Paid</span>
                        )}
                      </div>

                      {generatedLinkData.payment_confirmed || albumUtrSuccess ? (
                        <div style={{ fontSize: 12.5, color: "#166534" }}>
                          ✓ Confirmed via UTR: <strong>{generatedLinkData.utr || albumUtrNumber}</strong>
                        </div>
                      ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 4, width: "100%" }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4, width: "100%" }}>
                            <Input
                              placeholder="Enter 12-digit UTR reference"
                              value={albumUtrNumber}
                              onChange={(e) => setAlbumUtrNumber(e.target.value)}
                              style={{ height: 40, fontSize: 13, flex: 1, minWidth: 0 }}
                            />
                            <PrimaryButton
                              type="button"
                              onClick={handleConfirmAlbumUpi}
                              disabled={albumConfirmingUtr || albumUtrNumber.trim().replace(/\s+/g, "").length < 12}
                              style={{ width: "auto", minWidth: 130, height: 40, padding: "0 14px", flexShrink: 0 }}
                            >
                              {albumConfirmingUtr ? "Confirming..." : "Confirm Paid"}
                            </PrimaryButton>
                          </div>
                          {albumUtrNumber.trim().length > 0 && albumUtrNumber.trim().replace(/\s+/g, "").length < 12 && (
                            <div style={{ fontSize: 11, color: "#e11d48", fontWeight: 500 }}>
                              UTR number must be at least 12 digits ({albumUtrNumber.trim().replace(/\s+/g, "").length}/12)
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {verifiedStatus && (
                    <StatusVerifyBox $status={verifiedStatus}>
                      <span>Current Status:</span>
                      <strong>{verifiedStatus.toUpperCase()}</strong>
                    </StatusVerifyBox>
                  )}

                  <SuccessFooterActions>
                    <PrimaryButton
                      type="button"
                      onClick={() => {
                        onSuccess?.();
                        onClose();
                      }}
                      style={{ width: "100%" }}
                    >
                      Done
                    </PrimaryButton>
                  </SuccessFooterActions>
                </SuccessSection>
              )}
            </>
          )}

          {/* TAB 2: MANUAL PAYMENT ENTRY */}
          {activeTab === "manual" && (
            <form onSubmit={handleManualPaymentSubmit}>
              {manualSuccessMsg && (
                <SuccessBanner style={{ marginTop: 14 }}>
                  <SuccessIconWrap>
                    <CheckCircle size={20} />
                  </SuccessIconWrap>
                  <div>
                    <SuccessTitle>Recorded</SuccessTitle>
                    <SuccessSub>{manualSuccessMsg}</SuccessSub>
                  </div>
                </SuccessBanner>
              )}

              <FieldGrid style={{ marginTop: 16 }}>
                <Field>
                  <Label>
                    Amount (INR) <ReqStar>*</ReqStar>
                  </Label>
                  <Input
                    type="number"
                    placeholder="e.g. 5000"
                    value={manualAmount}
                    min={1}
                    onChange={(e) => setManualAmount(e.target.value)}
                    required
                  />
                </Field>

                <Field>
                  <Label>
                    Payment Method <ReqStar>*</ReqStar>
                  </Label>
                  <Select
                    value={manualMethod}
                    onChange={(e) => setManualMethod(e.target.value)}
                  >
                    {MANUAL_PAYMENT_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </Select>
                </Field>
              </FieldGrid>

              <FieldGrid style={{ marginTop: 14 }}>
                <Field>
                  <Label>Customer Name</Label>
                  <Input
                    type="text"
                    placeholder="e.g. Customer Name"
                    value={manualCustomerName}
                    onChange={(e) => setManualCustomerName(e.target.value)}
                  />
                </Field>
                <Field>
                  <Label>
                    Mobile Number
                    {manualCustomerName.trim() ? <ReqStar>*</ReqStar> : null}
                  </Label>
                  <Input
                    type="tel"
                    placeholder="10-digit mobile"
                    value={manualContact}
                    onChange={(e) => setManualContact(e.target.value)}
                    required={Boolean(manualCustomerName.trim())}
                  />
                </Field>
              </FieldGrid>

              <FieldGrid style={{ marginTop: 14 }}>
                <Field>
                  <Label>Transaction Reference (Optional)</Label>
                  <Input
                    type="text"
                    placeholder="e.g. CASH-20260915-001 or UPI Ref"
                    value={manualTxnRef}
                    onChange={(e) => setManualTxnRef(e.target.value)}
                  />
                </Field>

                <Field>
                  <Label>Payment Status</Label>
                  <Select
                    value={manualStatus}
                    onChange={(e) => setManualStatus(e.target.value)}
                  >
                    <option value="Completed">Completed</option>
                    <option value="Pending">Pending</option>
                  </Select>
                </Field>
              </FieldGrid>

              <Field style={{ marginTop: 14 }}>
                <Label>Description / Notes</Label>
                <Input
                  type="text"
                  placeholder="e.g. Manual payment received"
                  value={manualDesc}
                  onChange={(e) => setManualDesc(e.target.value)}
                />
              </Field>

              <ModalActions style={{ marginTop: 22 }}>
                <SecondaryButton type="button" onClick={onClose}>
                  Close
                </SecondaryButton>
                <PrimaryButton
                  type="submit"
                  disabled={manualLoading || !selectedAlbum || !manualAmount}
                >
                  {manualLoading ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>Recording...</span>
                    </>
                  ) : (
                    <>
                      <Receipt size={16} />
                      <span>Record Manual Payment</span>
                    </>
                  )}
                </PrimaryButton>
              </ModalActions>
            </form>
          )}

          {/* TAB 3: PAYMENT HISTORY */}
          {activeTab === "history" && (
            <HistorySection>
              {!selectedAlbum ? (
                <EmptyPrompt>
                  Please select an album customer above to view its payment history.
                </EmptyPrompt>
              ) : historyLoading ? (
                <LoadingWrap>
                  <Loader2 size={24} className="animate-spin" />
                  <span>Loading payment history...</span>
                </LoadingWrap>
              ) : (
                <>
                  {historyStats && (
                    <HistoryStatsGrid>
                      <StatMiniCard>
                        <StatMiniVal>{formatCurrency(historyStats.paid_amount || 0)}</StatMiniVal>
                        <StatMiniLabel>Paid Amount</StatMiniLabel>
                      </StatMiniCard>
                      <StatMiniCard>
                        <StatMiniVal>{formatCurrency(historyStats.pending_amount || 0)}</StatMiniVal>
                        <StatMiniLabel>Pending Amount</StatMiniLabel>
                      </StatMiniCard>
                      <StatMiniCard>
                        <StatMiniVal>{historyStats.paid_links || 0} / {historyStats.total_links || 0}</StatMiniVal>
                        <StatMiniLabel>Paid / Total Links</StatMiniLabel>
                      </StatMiniCard>
                    </HistoryStatsGrid>
                  )}

                  {historyItems.length === 0 ? (
                    <EmptyPrompt>
                      No payment links or records found for this album yet.
                    </EmptyPrompt>
                  ) : (
                    <HistoryTableWrap>
                      <Table>
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Amount</th>
                            <th>Method</th>
                            <th>Description</th>
                            <th>Status</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {historyItems.map((item) => (
                            <tr key={item.payment_id}>
                              <td>{formatDate(item.created_at)}</td>
                              <td>
                                <AmountCell>{formatCurrency(item.amount)}</AmountCell>
                              </td>
                              <td style={{ textTransform: "capitalize" }}>
                                {item.payment_method || "Online"}
                              </td>
                              <td>{item.description || "—"}</td>
                              <td>
                                <StatusPill $status={item.status}>
                                  {item.status?.toUpperCase() || "PENDING"}
                                </StatusPill>
                              </td>
                              <td>
                                {item.status !== "paid" && item.secret_token ? (
                                  <CheckStatusMiniBtn
                                    type="button"
                                    disabled={checkingHistoryId === item.payment_id}
                                    onClick={() => handleCheckHistoryItemStatus(item)}
                                  >
                                    <RefreshCw
                                      size={12}
                                      className={
                                        checkingHistoryId === item.payment_id ? "animate-spin" : ""
                                      }
                                    />
                                    <span>Check</span>
                                  </CheckStatusMiniBtn>
                                ) : (
                                  <span style={{ color: "#31a66a", fontSize: 12, fontWeight: 700 }}>
                                    ✓ Settled
                                  </span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </HistoryTableWrap>
                  )}
                </>
              )}
            </HistorySection>
          )}
        </ModalBody>
      </ModalShell>
    </ModalOverlay>,
    document.body
  );
}

/* Styled Components */

const ReqStar = styled.span`
  color: #e44862;
`;

const NavTabs = styled.div`
  display: flex;
  gap: 8px;
  background: rgba(23, 23, 23, 0.04);
  padding: 4px;
  border-radius: 12px;
  margin-bottom: 18px;
`;

const NavTabButton = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  border-radius: 9px;
  border: none;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  background: ${(p) => (p.$active ? "#ffffff" : "transparent")};
  color: ${(p) => (p.$active ? "#c94daa" : "rgba(23, 23, 23, 0.6)")};
  box-shadow: ${(p) => (p.$active ? "0 2px 8px rgba(0, 0, 0, 0.06)" : "none")};
  transition: all 0.15s ease;

  &:hover {
    color: #c94daa;
  }
`;

const AlbumSelectorSection = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const ChangeAlbumLink = styled.button`
  background: none;
  border: none;
  color: #c94daa;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  &:hover {
    text-decoration: underline;
  }
`;

const PickerInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchIconWrap = styled.div`
  position: absolute;
  left: 12px;
  color: rgba(23, 23, 23, 0.4);
  pointer-events: none;
  display: flex;
  align-items: center;
`;

const AlbumSearchInput = styled.input`
  width: 100%;
  height: 42px;
  padding: 0 40px 0 36px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fafafa;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #171717;
  transition: all 0.15s ease;

  &:focus {
    outline: none;
    border-color: #c94daa;
    background: #fff;
    box-shadow: 0 0 0 3px rgba(201, 77, 170, 0.12);
  }
`;

const SpinWrap = styled.div`
  position: absolute;
  right: 12px;
  color: #c94daa;
  display: flex;
  align-items: center;
  animation: spin 0.8s linear infinite;
  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const DropdownToggle = styled.button`
  position: absolute;
  right: 8px;
  background: none;
  border: none;
  color: rgba(23, 23, 23, 0.5);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
`;

const DropdownList = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 100;
  margin-top: 4px;
  max-height: 220px;
  overflow-y: auto;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
`;

const DropdownItem = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: none;
  border: none;
  border-bottom: 1px solid rgba(23, 23, 23, 0.04);
  text-align: left;
  cursor: pointer;
  transition: background 0.1s ease;

  &:hover {
    background: rgba(201, 77, 170, 0.05);
  }

  &:last-child {
    border-bottom: none;
  }
`;

const DropdownItemLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const DropdownItemTitle = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #171717;
`;

const DropdownItemSub = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  color: rgba(23, 23, 23, 0.55);
`;

const DropdownItemRight = styled.div``;

const PaidPill = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 999px;
  background: rgba(49, 166, 106, 0.1);
  color: #31a66a;
`;

const EmptyDropdown = styled.div`
  padding: 16px;
  text-align: center;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.5);
`;

const SelectedAlbumCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: linear-gradient(135deg, rgba(255, 201, 81, 0.1) 0%, rgba(206, 86, 176, 0.08) 100%);
  border: 1px solid rgba(201, 77, 170, 0.2);
  border-radius: 14px;
`;

const AlbumCardLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const AlbumIconBadge = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(201, 77, 170, 0.15);
  color: #c94daa;
`;

const AlbumInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const AlbumNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const AlbumName = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: #171717;
`;

const AlbumIdBadge = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
  background: rgba(23, 23, 23, 0.06);
  padding: 2px 6px;
  border-radius: 4px;
`;

const CustomerMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.65);
`;

const DotSeparator = styled.span`
  color: rgba(23, 23, 23, 0.3);
`;

const AlbumPaidSummary = styled.div`
  text-align: right;
`;

const SummaryAmount = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: #31a66a;
`;

const SummaryLabel = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
`;

const FeeBreakdownBox = styled.div`
  min-height: 48px;
  box-sizing: border-box;
  padding: 8px 14px;
  border-radius: 12px;
  background: linear-gradient(135deg, rgba(255, 201, 81, 0.08) 0%, rgba(201, 77, 170, 0.06) 100%);
  border: 1px dashed rgba(201, 77, 170, 0.25);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
`;

const FeeRow = styled.div<{ $payout?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-family: Montserrat, sans-serif;
  font-size: ${(props) => (props.$payout ? "12px" : "11px")};
  color: ${(props) => (props.$payout ? "#171717" : "rgba(23, 23, 23, 0.62)")};
  font-weight: ${(props) => (props.$payout ? "600" : "500")};
  ${(props) =>
    props.$payout &&
    `
    padding-top: 4px;
    border-top: 1px dashed rgba(201, 77, 170, 0.16);
  `}
`;

const FeeRowLabel = styled.span`
  display: inline-flex;
  align-items: center;
`;

const FeeRowValue = styled.span`
  font-weight: 600;
  color: rgba(23, 23, 23, 0.72);
  font-family: Urbanist, sans-serif;
  font-size: 13px;
`;

const PayoutAmount = styled.span<{ $valid?: boolean }>`
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 800;
  color: ${(props) => (props.$valid ? "#31a66a" : "rgba(23, 23, 23, 0.35)")};
`;

const Select = styled.select`
  width: 100%;
  height: 42px;
  padding: 0 14px;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #fafafa;
  font-family: Urbanist, sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: #171717;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #c94daa;
    background: #fff;
  }
`;

const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 10px;
  background: rgba(228, 72, 98, 0.08);
  border: 1px solid rgba(228, 72, 98, 0.2);
  color: #e44862;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 600;
  margin-top: 12px;
`;

const SuccessSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 16px;
`;

const SuccessBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 12px;
  background: rgba(49, 166, 106, 0.08);
  border: 1px solid rgba(49, 166, 106, 0.2);
`;

const SuccessIconWrap = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(49, 166, 106, 0.15);
  color: #31a66a;
  flex-shrink: 0;
`;

const SuccessTitle = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: #171717;
`;

const SuccessSub = styled.div`
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  color: rgba(23, 23, 23, 0.6);
`;

const LinkBoxWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #f7f7f8;
  border: 1px solid rgba(23, 23, 23, 0.1);
`;

const LinkBoxText = styled.div`
  flex: 1;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  font-weight: 600;
  color: #171717;
  word-break: break-all;
`;

const CopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 6px 12px;
  border-radius: 8px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #ffffff;
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #171717;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    border-color: #c94daa;
    color: #c94daa;
  }
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const ActionBtn = styled.button<{ $tone?: string; $solid?: boolean; $active?: boolean }>`
  min-height: 40px;
  border-radius: 10px;
  border: 1px solid ${(p) => (p.$solid ? "transparent" : "rgba(23, 23, 23, 0.12)")};
  background: ${(p) => (p.$solid ? p.$tone || "#25d366" : p.$active ? "rgba(201, 77, 170, 0.1)" : "#fff")};
  color: ${(p) => (p.$solid ? "#fff" : p.$active ? "#c94daa" : "rgba(23, 23, 23, 0.75)")};
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: all 0.15s ease;

  &:hover {
    border-color: ${(p) => (p.$solid ? "transparent" : "#c94daa")};
    color: ${(p) => (p.$solid ? "#fff" : "#c94daa")};
  }
`;

const StatusVerifyBox = styled.div<{ $status: string }>`
  padding: 8px 12px;
  border-radius: 8px;
  background: ${(p) =>
    p.$status.toLowerCase() === "paid" ? "rgba(49, 166, 106, 0.1)" : "rgba(240, 154, 62, 0.1)"};
  color: ${(p) => (p.$status.toLowerCase() === "paid" ? "#31a66a" : "#f09a3e")};
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const QrWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  border-radius: 14px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.08);
`;

const SuccessFooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 14px;
  width: 100%;
`;

const HistorySection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  margin-top: 14px;
`;

const HistoryStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
`;

const StatMiniCard = styled.div`
  padding: 10px 12px;
  border-radius: 10px;
  background: #fafafa;
  border: 1px solid rgba(23, 23, 23, 0.06);
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const StatMiniVal = styled.span`
  font-family: Urbanist, sans-serif;
  font-size: 15px;
  font-weight: 800;
  color: #171717;
`;

const StatMiniLabel = styled.span`
  font-family: Montserrat, sans-serif;
  font-size: 10px;
  font-weight: 600;
  color: rgba(23, 23, 23, 0.5);
`;

const HistoryTableWrap = styled.div`
  max-height: 240px;
  overflow-y: auto;
  border-radius: 12px;
  border: 1px solid rgba(23, 23, 23, 0.08);
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th {
    padding: 8px 10px;
    background: #f7f7f8;
    font-family: Montserrat, sans-serif;
    font-size: 11px;
    font-weight: 700;
    color: rgba(23, 23, 23, 0.6);
    text-align: left;
    border-bottom: 1px solid rgba(23, 23, 23, 0.08);
  }

  td {
    padding: 8px 10px;
    font-family: Montserrat, sans-serif;
    font-size: 12px;
    border-bottom: 1px solid rgba(23, 23, 23, 0.04);
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const CheckStatusMiniBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 6px;
  border: 1px solid rgba(23, 23, 23, 0.12);
  background: #ffffff;
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #171717;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #c94daa;
    color: #c94daa;
  }
`;

const EmptyPrompt = styled.div`
  padding: 32px 16px;
  text-align: center;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
  color: rgba(23, 23, 23, 0.5);
`;

const LoadingWrap = styled.div`
  padding: 32px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #c94daa;
  font-family: Montserrat, sans-serif;
  font-size: 13px;
`;

const HeroLeftWrap = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  min-width: 0;
  flex: 1;
`;

const HeaderBackBtn = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(23, 23, 23, 0.1);
  background: rgba(255, 255, 255, 0.85);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: rgba(23, 23, 23, 0.6);
  flex-shrink: 0;
  margin-top: 2px;
  transition: all 0.15s ease;
  &:hover {
    color: #c94daa;
    border-color: rgba(201, 77, 170, 0.25);
    background: #fff;
  }
`;

const OptionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const OptionCard = styled.div<{ $selected?: boolean; $tone?: "emerald" | "purple" }>`
  position: relative;
  padding: 16px 18px;
  border-radius: 16px;
  border: 2px solid
    ${(p) =>
      p.$selected
        ? p.$tone === "emerald"
          ? "#31a66a"
          : "#c94daa"
        : "rgba(23, 23, 23, 0.08)"};
  background: ${(p) =>
    p.$selected
      ? p.$tone === "emerald"
        ? "rgba(49, 166, 106, 0.04)"
        : "rgba(201, 77, 170, 0.04)"
      : "#ffffff"};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${(p) =>
      p.$tone === "emerald" ? "#31a66a" : "#c94daa"};
    transform: translateY(-1px);
  }
`;

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const OptionTitleWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const OptionIcon = styled.div<{ $tone: "emerald" | "purple" }>`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => (p.$tone === "emerald" ? "rgba(49, 166, 106, 0.12)" : "rgba(201, 77, 170, 0.12)")};
  color: ${(p) => (p.$tone === "emerald" ? "#31a66a" : "#c94daa")};
`;

const OptionTitle = styled.div`
  font-family: Urbanist, sans-serif;
  font-size: 16px;
  font-weight: 800;
  color: #171717;
`;

const OptionBadge = styled.span<{ $tone: "emerald" | "purple" }>`
  font-family: Montserrat, sans-serif;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 20px;
  background: ${(p) => (p.$tone === "emerald" ? "rgba(49, 166, 106, 0.15)" : "rgba(201, 77, 170, 0.15)")};
  color: ${(p) => (p.$tone === "emerald" ? "#227b4e" : "#a8358b")};
`;

const OptionDesc = styled.p`
  margin: 0 0 10px;
  font-family: Montserrat, sans-serif;
  font-size: 12.5px;
  color: rgba(23, 23, 23, 0.65);
  line-height: 1.45;
`;

const OptionAmountRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 10px;
  border-top: 1px dashed rgba(23, 23, 23, 0.1);
  font-family: Montserrat, sans-serif;
  font-size: 12.5px;
  color: rgba(23, 23, 23, 0.7);

  strong {
    font-family: Urbanist, sans-serif;
    font-size: 16px;
    font-weight: 800;
    color: #171717;
  }
`;

const UpiVpaBox = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(23, 23, 23, 0.03);
  border: 1px solid rgba(23, 23, 23, 0.08);
  font-family: Montserrat, sans-serif;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: rgba(23, 23, 23, 0.8);
`;
